import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

const getRoleFromGroups = (groups) => {
  if (groups.includes('Supervisor')) return 'Supervisor';
  if (groups.includes('Technician')) return 'Technician';
  return 'PlantDirector';
};

const cognitoService = {
  login: ({ username, password }) => {
    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => {
          const idToken = result.getIdToken().getJwtToken();
          const accessToken = result.getAccessToken().getJwtToken();
          const payload = result.getIdToken().decodePayload();
          const accessPayload = result.getAccessToken().decodePayload();
          const groups = accessPayload['cognito:groups'] || [];

          resolve({
            token: idToken,
            accessToken,
            user: {
              username: payload['cognito:username'] || payload.sub,
              email: payload.email || '',
              name: payload.name || payload['cognito:username'] || '',
              role: getRoleFromGroups(groups),
            },
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          // Resolve with a challenge flag so the UI can redirect
          resolve({
            challengeName: 'NEW_PASSWORD_REQUIRED',
            cognitoUser,
            userAttributes,
          });
        },
      });
    });
  },

  completeNewPassword: (cognitoUser, newPassword, requiredAttributes) => {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
        onSuccess: (result) => {
          const idToken = result.getIdToken().getJwtToken();
          const accessToken = result.getAccessToken().getJwtToken();
          const payload = result.getIdToken().decodePayload();
          const accessPayload = result.getAccessToken().decodePayload();
          const groups = accessPayload['cognito:groups'] || [];

          resolve({
            token: idToken,
            accessToken,
            user: {
              username: payload['cognito:username'] || payload.sub,
              email: payload.email || '',
              name: payload.name || payload['cognito:username'] || '',
              role: getRoleFromGroups(groups),
            },
          });
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  },

  logout: () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    return Promise.resolve();
  },

  getCurrentUser: () => {
    return new Promise((resolve, reject) => {
      const currentUser = userPool.getCurrentUser();
      if (!currentUser) {
        reject(new Error('No user session'));
        return;
      }

      currentUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          reject(err || new Error('Session expired'));
          return;
        }

        const payload = session.getIdToken().decodePayload();
        const accessPayload = session.getAccessToken().decodePayload();
        const groups = accessPayload['cognito:groups'] || [];
        resolve({
          token: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          user: {
            username: payload['cognito:username'] || payload.sub,
            email: payload.email || '',
            name: payload.name || payload['cognito:username'] || '',
            role: getRoleFromGroups(groups),
          },
        });
      });
    });
  },

  getIdToken: () => {
    return new Promise((resolve, reject) => {
      const currentUser = userPool.getCurrentUser();
      if (!currentUser) {
        reject(new Error('No user session'));
        return;
      }

      currentUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          reject(err || new Error('Session expired'));
          return;
        }
        resolve(session.getIdToken().getJwtToken());
      });
    });
  },
};

export default cognitoService;
